import { Lock } from "lucide-react";

/**
 * Client-side catalog for thread flags (API stores plain strings in metadata.flags).
 *
 * Since the thread-management redesign the catalog holds ONLY the AI-handling flag `locked`,
 * which pauses the concierge bot and surfaces the thread to the team (`staff_handles` is
 * retired — DISABLED for now — and resolves to no definition like any stale flag). The old
 * descriptive flags (important/escalated/waiting/vip/unpaid) were replaced by
 * patient-level tags (see src/lib/patient-tags.js, stored on the patient record); stale flag
 * strings may persist in old thread metadata and simply resolve to no definition.
 *
 * Each entry renders in three surfaces:
 *   - row strip      → 14×14 icon glyph (see glyphClassName)
 *   - context menu   → labelled chip (see chipClassName)
 *   - focused views  → same chip with icon alongside label
 */
/**
 * Shown first in the thread context menu, separated from the rest (most commonly toggled).
 * DISABLED (for now): staff_handles is hidden everywhere (nothing applies it anymore);
 * legacy-flagged threads still pause the bot via isThreadAiPaused and can be cleared
 * with "Bot answering".
 */
export const THREAD_FLAG_CONTEXT_MENU_PRIMARY_SHORTNAMES = Object.freeze(["locked"]);

export const THREAD_FLAG_CATALOG = [
  {
    shortName: "locked",
    displayName: "Locked",
    icon: Lock,
    tier: "muted",
    glyphClassName: "text-violet-500",
    chipClassName:
      "border border-violet-300 bg-violet-100 text-violet-900 dark:border-violet-500/40 dark:bg-violet-950/60 dark:text-violet-100",
  },
  // DISABLED (for now): hiding the catalog entry removes the row glyph and the
  // context-menu chip; stale flag strings resolve to no definition by design.
  // {
  //   shortName: "staff_handles",
  //   displayName: "Staff handles",
  //   icon: UserRound,
  //   tier: "notice",
  //   glyphClassName: "text-emerald-500",
  //   chipClassName:
  //     "border border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-950/60 dark:text-emerald-100",
  // },
];

const byShortName = Object.fromEntries(THREAD_FLAG_CATALOG.map((def) => [def.shortName, def]));

const primaryShortNameSet = new Set(THREAD_FLAG_CONTEXT_MENU_PRIMARY_SHORTNAMES);

/** Context menu: primary row (locked + staff handles) vs other flags, for layout separation. */
export function getThreadFlagCatalogMenuSections() {
  const primary = THREAD_FLAG_CONTEXT_MENU_PRIMARY_SHORTNAMES.map((n) => byShortName[n]).filter(Boolean);
  const rest = THREAD_FLAG_CATALOG.filter((def) => !primaryShortNameSet.has(def.shortName));
  return { primary, rest };
}

export function getThreadFlagStrings(thread) {
  const raw = thread?.metadata?.flags;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((f) => String(f || "").trim()).filter(Boolean);
}

export function getFlagDefinition(shortName) {
  const key = String(shortName || "").trim();
  return byShortName[key];
}

/**
 * True when the AI-handling pause flag is set — the bot is paused and a human must reply.
 * DISABLED (for now): staff_handles is retired and no longer counts as paused.
 */
export function isThreadAiPaused(thread) {
  const flags = getThreadFlagStrings(thread);
  return flags.includes("locked") /* || flags.includes("staff_handles") */;
}

/**
 * The reason recorded when the thread was locked (e.g. "out_of_service"), persisted by the
 * backend to metadata.lockReason while `locked` is on. Empty string when absent — render
 * nothing in that case.
 */
export function getThreadLockReason(thread) {
  const raw = thread?.metadata?.lockReason;
  return typeof raw === "string" ? raw.trim() : "";
}

/**
 * Display form of a lock reason / auto-task origin: machine codes like "out_of_service"
 * read as "out of service". Stored values stay untouched — this is render-time only.
 */
export function humanizeLockReason(reason) {
  return String(reason || "")
    .trim()
    .replace(/_+/g, " ")
    .replace(/\s+/g, " ");
}
